/**
 * Error transformation utility for PayLOGIK React application
 *
 * Transforms RTK Query errors into semantic error categories with
 * user-friendly messages and retry logic.
 */

import {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {ErrorCategory, AppError} from '../types/errors';

/**
 * Type guard to check if an error is a FetchBaseQueryError from RTK Query
 */
export function isFetchBaseQueryError(
    error: unknown
): error is FetchBaseQueryError {
    return typeof error === 'object' && error != null && 'status' in error;
}

/**
 * Transform RTK Query error to standardized AppError with semantic category
 *
 * Maps HTTP status codes and error types to user-friendly categories:
 * - FETCH_ERROR, TIMEOUT_ERROR → Network (retryable)
 * - 401, 403 → Auth (not retryable)
 * - 400-499 → Client (not retryable)
 * - 500-599 → Server (retryable)
 * - Unknown → Unknown (retryable)
 *
 * @param error - Error from RTK Query or other source
 * @param source - Where the error originated (component name, API endpoint)
 * @returns Standardized AppError with category, message, and retry info
 */
export function transformError(error: unknown, source: string): AppError {
    const timestamp = new Date();

    // Handle RTK Query FetchBaseQueryError
    if (isFetchBaseQueryError(error)) {
        // Network connection errors
        if (error.status === 'FETCH_ERROR') {
            return {
                category: ErrorCategory.Network,
                message:
                    'Unable to connect. Please check your internet connection.',
                originalError: error,
                timestamp,
                isRetryable: true,
                retryCount: 0,
                source,
            };
        }

        // Request timeout errors
        if (error.status === 'TIMEOUT_ERROR') {
            return {
                category: ErrorCategory.Network,
                message: 'Request timed out. Please try again.',
                originalError: error,
                timestamp,
                isRetryable: true,
                retryCount: 0,
                source,
            };
        }

        // Parsing errors from server
        if (error.status === 'PARSING_ERROR') {
            return {
                category: ErrorCategory.Server,
                message: 'Server returned invalid data. Please try again.',
                originalError: error,
                timestamp,
                isRetryable: true,
                retryCount: 0,
                source,
            };
        }

        // HTTP status code errors
        if (typeof error.status === 'number') {
            // Authentication/authorization errors
            if (error.status === 401 || error.status === 403) {
                return {
                    category: ErrorCategory.Auth,
                    message: 'Your session has expired. Please log in again.',
                    originalError: error,
                    timestamp,
                    isRetryable: false,
                    retryCount: 0,
                    source,
                };
            }

            // Client errors (validation, bad request, not found)
            if (error.status >= 400 && error.status < 500) {
                return {
                    category: ErrorCategory.Client,
                    message: 'Invalid request. Please check your input.',
                    originalError: error,
                    timestamp,
                    isRetryable: false,
                    retryCount: 0,
                    source,
                };
            }

            // Server errors (internal error, service unavailable)
            if (error.status >= 500 && error.status < 600) {
                return {
                    category: ErrorCategory.Server,
                    message: 'Server error. Please try again in a moment.',
                    originalError: error,
                    timestamp,
                    isRetryable: true,
                    retryCount: 0,
                    source,
                };
            }
        }
    }

    // Fallback for unknown errors
    return {
        category: ErrorCategory.Unknown,
        message: 'An unexpected error occurred. Please try again.',
        originalError:
            error instanceof Error ? error : new Error(String(error)),
        timestamp,
        isRetryable: true,
        retryCount: 0,
        source,
    };
}
